import Wrapper from 'components/wrapper';
import { fetchData } from 'lib/fetchData';
import { markdownToHtml } from 'lib/markdownToHtml';
import type { GetStaticPaths, GetStaticProps, NextPage } from 'next';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useRouter } from 'next/router';
import * as React from 'react';
import { API } from '_helpers';

const TrainingLayout = dynamic(
  import('components/ourTrainings/components/trainingLayout')
);
const Header = dynamic(import('components/header'));

const CourseDetail = dynamic(
  import('components/ourTrainings/components/trainingDetail')
);

interface IProps {
  header: IHeaders;
  menu: any;
  footer: any;
  company: any;
  course: any;
}

const Course: NextPage<IProps> = (props) => {
  const { menu, header, footer, company, course } = props;
  const { asPath, locale } = useRouter();

  return (
    <Wrapper menu={menu} company={company} footer={footer}>
      <Head>
        <title>{course.title}</title>
        <meta name="description" content={course.description} />
        <link rel="icon" href={API + company.favicon.media.url} />
        <meta name="robots" content="index,follow" />
        <meta name="twitter:dnt" content="on" />
        <meta
          property="article:published_time"
          content={new Date().toLocaleTimeString()}
        />
        <meta
          name="publish_date"
          property="og:publish_date"
          content={new Date().toLocaleDateString()}
        />
        <meta
          name="author"
          property="article:author"
          content="Luccin Masirika"
        />
        {/* Open Graph */}
        <meta property="og:title" content={course.title} />
        <meta property="og:description" content={course.description} />
        <meta
          property="og:url"
          content={`https://uptodatedevelopers.com${asPath}`}
        />
        <meta property="og:image" content={API + course.media.url} />
        <meta property="og:site_name" content={company.name} />
        <meta property="og:locale" content={locale} />
        <meta property="og:type" content="website" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content={company.name} />
      </Head>
      <Header data={header.trainings} />
      <TrainingLayout>
        <CourseDetail data={course} />
      </TrainingLayout>
    </Wrapper>
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  const fr = await fetchData(`/courses/?_locale=fr`);
  const en = await fetchData(`/courses/?_locale=en`);

  const pathsFr = fr.map((el: any) => {
    return { params: { slug: el.slug }, locale: 'fr' };
  });

  const pathsEn = en.map((el: any) => {
    return { params: { slug: el.slug }, locale: 'en' };
  });

  const paths = [...pathsEn, ...pathsFr];

  return {
    paths,
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps = async ({ locale, params }) => {
  const course = await fetchData(
    `/courses/?_locale=${locale}&slug=${params?.slug}`
  );
  const content = await markdownToHtml(course[0]?.content);
  const menu = await fetchData(`/menu/?_locale=${locale}`);
  const company = await fetchData(`/company/?_locale=${locale}`);
  const footer = await fetchData(`/footer/?_locale=${locale}`);
  const header = await fetchData(`/headers/?_locale=${locale}`);

  if (course.length === 0) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      menu,
      company,
      footer,
      header,
      course: { ...course[0], content },
    },
    revalidate: 10,
  };
};

export default Course;
